import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import * as Icons from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  attend_present: "Attend Class (Present)", attend_late: "Attend (Late)", attend_excused: "Attend (Excused)",
  replay_watched: "Watch Replay", replay_completed: "Complete Replay", quest_complete: "Complete Quest",
  quest_approved: "Quest Approved Bonus", bonus_challenge: "Bonus Challenge", help_champ: "Help Another Champ",
  consistency: "Consistent Participation", teacher_award: "Teacher Special Award", badge_unlock: "Badge Unlocked",
  performance_rehearsal: "Performance Rehearsal",
};

export default function PointRules() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["setting", "point_rules"], queryFn: async () => (await api.get("/settings/point_rules")).data });
  const { data: lb } = useQuery({ queryKey: ["setting", "leaderboard_enabled"], queryFn: async () => (await api.get("/settings/leaderboard_enabled")).data });
  const [rules, setRules] = useState({});
  const [lbOn, setLbOn] = useState(true);

  useEffect(() => { if (data?.value) setRules(data.value); }, [data]);
  useEffect(() => { if (lb) setLbOn(lb.value); }, [lb]);

  if (isLoading) return <Loader />;

  const save = async () => {
    const clean = {}; Object.keys(rules).forEach((k) => { clean[k] = Number(rules[k]); });
    await api.put("/settings/point_rules", clean);
    toast.success("Point rules saved! 🎯"); qc.invalidateQueries();
  };
  const toggleLb = async (v) => { setLbOn(v); await api.put("/settings/leaderboard_enabled", { enabled: v }); toast.success(`Leaderboard ${v ? "enabled" : "disabled"}`); qc.invalidateQueries(); };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="point-rules">
      <h2 className="font-head text-3xl text-csh-blue">Point Rule Builder ⚙️</h2>
      <p className="text-muted-foreground">Customize how many Story Points each action awards. Nothing is hard-coded!</p>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5 space-y-2">
        {Object.keys(LABELS).map((k) => (
          <div key={k} className="flex items-center justify-between gap-3 border-b border-black/5 py-2">
            <label className="font-semibold text-csh-grey">{LABELS[k]}</label>
            <Input type="number" value={rules[k] ?? 0} onChange={(e) => setRules({ ...rules, [k]: e.target.value })} className="w-28 rounded-xl border-2 text-center" data-testid={`rule-${k}`} />
          </div>
        ))}
        <Button onClick={save} className="mt-3 w-full rounded-full gradient-orange py-6 font-bold text-white" data-testid="save-rules-btn"><Icons.Save size={16} /> Save Point Rules</Button>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5 flex items-center justify-between">
        <div>
          <h3 className="font-head text-xl text-csh-blue">Leaderboard</h3>
          <p className="text-sm text-muted-foreground">Show or hide the leaderboard for students.</p>
        </div>
        <Switch checked={lbOn} onCheckedChange={toggleLb} data-testid="lb-toggle" />
      </div>
    </div>
  );
}
